/**
 * F16.G.2: full Win95 Desktop inside a portal. Mounts the existing
 * `<Win95Desktop>` (with ExplorerWindow, PromptLab, SkillLibrary,
 * Settings, Help, etc.) under the portal's branded chrome. The desktop
 * background picks up `--desktop-bg` from the portal theme — no extra
 * prop plumbing required.
 *
 * F18.demo.4.2 — Auto-opens a window when the URL carries a `?panel=`
 * search param. Used by the post-onboarding "bring-your-own" buttons:
 *   - panel=connect-sharepoint → opens /admin/workspace-setup
 *   - panel=obsidian-import    → opens /obsidian-import
 *   - panel=document-library   → opens /documents
 *   - panel=skills             → opens /skills
 * Unknown / missing panel → no-op.
 *
 * Why this exists: porting every Win95 window into a separate portal
 * surface would duplicate ~5K lines of code. Mounting the existing
 * desktop inside `/c/$slug/desktop` lets a portal user see every
 * Repancy feature with the firm's branding applied as the wallpaper.
 *
 * Layout:
 *   - `TenantChrome` (parent route) paints the title bar at the top.
 *   - This route renders `<Win95Desktop>` below it. Win95Desktop uses
 *     `position: fixed; inset: 0;` so it covers the viewport — that's
 *     OK because the portal's TenantChrome header is fixed/floating in
 *     z-index terms; users can still click "Sign in / out" up top.
 */

import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Win95Desktop } from '../../../components/desktop/Win95Desktop';
import { useWindowContext } from '../../../components/desktop/WindowContext';
import {
  SETTINGS_SET_TAB_EVENT,
  type SettingsSetTabEventDetail,
} from '../../../components/settings/SettingsWindow';
import type { SettingsTab } from '../../../components/settings/SettingsWindow';
import { useTenantBranding } from '../../../lib/tenant/TenantBrandingContext';
import { testIds } from '../../../lib/testIds';

/**
 * F18.demo.4.4 — supported `?panel=...` values + their resolution.
 *
 * - `windowPath` is the desktop window to openWindow().
 * - `settingsTab` optionally pre-selects a tab inside SettingsWindow
 *   via the SETTINGS_SET_TAB_EVENT after the window mounts. This lets
 *   the wizard's "Connect my SharePoint sites" button land the user
 *   on the right tab without rebuilding SettingsWindow's API.
 */
const PANEL_TO_TARGET: Record<
  string,
  { windowPath: string; settingsTab?: SettingsTab }
> = {
  'connect-sharepoint': { windowPath: '/settings', settingsTab: 'workspace' },
  'obsidian-import': { windowPath: '/obsidian-import' },
  'document-library': { windowPath: '/documents' },
  'skills': { windowPath: '/skills' },
};

// Exported so TanStack's generated routeTree.gen.ts can name the
// inferred search-param type on the Route's exported binding.
export interface DesktopSearchParams {
  panel?: string;
  /**
   * 2026-05-28 — selects a specific vault for ObsidianVaultWindow.
   * The import wizard's "Open vault" CTA sets this to the
   * freshly-committed vaultId so the user lands on THEIR import
   * even when older duplicates exist. Reading this synchronously
   * via useSearch() in the vault window beats sessionStorage /
   * CustomEvent handoffs: it's race-free, refresh-friendly, and
   * shareable.
   */
  vault?: string;
}

/**
 * Tiny child consumer that reads the panel param + opens the right
 * window after Win95Desktop's registerWindow effect has run. Must live
 * INSIDE Win95Desktop so it sees the WindowProvider context.
 *
 * F18.demo.4.4 — when the target has a settingsTab, dispatch the
 * SETTINGS_SET_TAB_EVENT after openWindow so SettingsWindow's listener
 * can pre-select the right tab.
 */
function PanelAutoOpener({ panel }: { panel: string | null }) {
  const { openWindow, getWindow } = useWindowContext();
  const handledRef = useRef(false);
  useEffect(() => {
    // Loud-log every branch — silent returns hid a real bug where the
    // panel never opened and there was nothing in the console to look at.
    if (handledRef.current) {
      console.debug('[PanelAutoOpener] already handled; skip', { panel });
      return;
    }
    if (!panel) {
      console.debug('[PanelAutoOpener] no panel query param; nothing to do');
      return;
    }
    const target = PANEL_TO_TARGET[panel];
    if (!target) {
      console.warn(
        `[PanelAutoOpener] unsupported panel="${panel}"; valid: ${Object.keys(PANEL_TO_TARGET).join(', ')}`,
      );
      return;
    }
    console.info(
      `[PanelAutoOpener] panel="${panel}" → opening ${target.windowPath}`,
      target.settingsTab ? `(tab=${target.settingsTab})` : '',
    );
    let attempts = 0;
    const MAX_ATTEMPTS = 40; // 40 × 50ms = 2s
    const tryOpen = (): boolean => {
      attempts += 1;
      const registered = getWindow(target.windowPath);
      if (!registered) {
        if (attempts === 1 || attempts === MAX_ATTEMPTS) {
          console.debug(
            `[PanelAutoOpener] attempt ${attempts}/${MAX_ATTEMPTS}: window ${target.windowPath} not registered yet`,
          );
        }
        return false;
      }
      openWindow(target.windowPath);
      console.info(
        `[PanelAutoOpener] opened ${target.windowPath} after ${attempts} attempt(s)`,
      );
      if (target.settingsTab) {
        window.setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent<SettingsSetTabEventDetail>(SETTINGS_SET_TAB_EVENT, {
              detail: { tab: target.settingsTab! },
            }),
          );
          console.info(
            `[PanelAutoOpener] dispatched ${SETTINGS_SET_TAB_EVENT} tab=${target.settingsTab}`,
          );
        }, 50);
      }
      handledRef.current = true;
      return true;
    };
    if (tryOpen()) return;
    const interval = setInterval(() => {
      if (tryOpen()) clearInterval(interval);
    }, 50);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!handledRef.current) {
        console.error(
          `[PanelAutoOpener] gave up after ${MAX_ATTEMPTS} attempts: window ${target.windowPath} never registered. Check Win95Desktop.tsx:registerWindow("${target.windowPath}", ...).`,
        );
      }
    }, 2000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [panel, openWindow, getWindow]);
  return null;
}

function PortalDesktopRoute() {
  const { slug, branding } = useTenantBranding();
  const logoUrl = useQuery(api.tenantBranding.getLogoUrl, { clientSlug: slug });
  const search = useSearch({ from: '/c/$clientSlug/desktop' });
  const panel = search.panel ?? null;

  // Apply --desktop-bg-image to the portal root for the lifetime of this
  // route. We target the route's nearest [data-portal-theme] ancestor
  // (painted by TenantChrome) so the wallpaper variable scopes only to
  // this portal — never bleeds onto the default / SPA.
  useEffect(() => {
    if (!logoUrl) {return;}
    const host = document.querySelector<HTMLElement>('[data-portal-theme]');
    if (!host) {return;}
    const prev = host.style.getPropertyValue('--desktop-bg-image');
    host.style.setProperty('--desktop-bg-image', `url(${JSON.stringify(logoUrl)})`);
    return () => {
      if (prev) {
        host.style.setProperty('--desktop-bg-image', prev);
      } else {
        host.style.removeProperty('--desktop-bg-image');
      }
    };
  }, [logoUrl]);

  // F18.G.1+ — apply the portal's chosen primaryColor as the desktop
  // wallpaper. Without this the user-picked color in the F18 firm-
  // setup step ONLY landed on the chrome border accent — the desktop
  // background fell back to the hardcoded forest-green gradient
  // (xp-luna-overrides.css `data-portal-provisioned='onboarding'`)
  // regardless of what the user chose. Build a 3-stop gradient from
  // the user's hex so it still reads as "an XP wallpaper" (diagonal,
  // 3 tonal stops) rather than a flat fill.
  useEffect(() => {
    const host = document.querySelector<HTMLElement>('[data-portal-theme]');
    if (!host) return;
    const color = branding.primaryColor;
    if (!color || typeof color !== 'string' || color.length === 0) return;
    const prev = host.style.getPropertyValue('--portal-wallpaper');
    // Three-stop diagonal — light → mid (user color) → dark. The dark
    // variant uses `color-mix` against black to derive a deeper stop
    // without hard-coding per-color values.
    host.style.setProperty(
      '--portal-wallpaper',
      `linear-gradient(135deg, ` +
        `color-mix(in srgb, ${color} 70%, white) 0%, ` +
        `${color} 50%, ` +
        `color-mix(in srgb, ${color} 60%, black) 100%)`,
    );
    return () => {
      if (prev) {
        host.style.setProperty('--portal-wallpaper', prev);
      } else {
        host.style.removeProperty('--portal-wallpaper');
      }
    };
  }, [branding.primaryColor]);

  return (
    <div data-testid={testIds.portal.desktopHost} className="relative">
      <Win95Desktop>
        {/* F18.demo.4.2 — Tiny consumer that auto-opens the panel
            window when the URL carries ?panel=. Must live inside
            Win95Desktop to access the WindowProvider context. */}
        <PanelAutoOpener panel={panel} />
      </Win95Desktop>
    </div>
  );
}

export const Route = createFileRoute('/c/$clientSlug/desktop')({
  component: PortalDesktopRoute,
  validateSearch: (search: Record<string, unknown>): DesktopSearchParams => ({
    panel: typeof search.panel === 'string' ? search.panel : undefined,
    vault: typeof search.vault === 'string' ? search.vault : undefined,
  }),
});
